<template>
  <div class="container p0">
    <div id="checkout">
      <div v-if="!orderComplete" class="checkout">
        <div class="checkout__main">
          <div v-if="!isLoggedIn" class="log-in">
            <SfButton class="log-in__button color-secondary" @click="login">
              Login / Register
            </SfButton>
            <p class="log-in__info">{{ $t("or fill the details below") }}:</p>
          </div>
          <SfHeading
            title="Personal details"
            :level="2"
            class="sf-heading--left sf-heading--no-underline title"
          />
          <div class="form checkout__form">
            <SfInput
              v-model.trim="personalDetails.fullName"
              :value="personalDetails.fullName"
              label="Full name"
              name="fullName"
              class="form__element form__element--half"
              :required="true"
              :valid="!$v.personalDetails.fullName.$error"
              :error-message="
                !$v.personalDetails.fullName.required
                  ? $t('Field is required')
                  : $t('Name must have at least 2 letters.')
              "
              @blur="$v.personalDetails.fullName.$touch()"
            />
            <SfInput
              v-model.trim="personalDetails.email"
              :value="personalDetails.email"
              label="Your email"
              name="email"
              class="form__element"
              :required="true"
              :valid="!$v.personalDetails.email.$error"
              :error-message="
                !$v.personalDetails.email.required
                  ? $t('Field is required')
                  : $t('Please provide valid e-mail address.')
              "
              @blur="$v.personalDetails.email.$touch()"
            />
            <PhoneInput
              v-model="personalDetails.phone"
              :value="personalDetails.phone"
              label="Phone number"
              name="phone"
              class="form__element"
              :required="true"
              :valid="!$v.personalDetails.phone.$error"
              :error-message="
                !$v.personalDetails.phone.required
                  ? $t('Field is required')
                  : 'Please provide valid phone number.'
              "
              @blur="$v.personalDetails.phone.$touch()"
              @validate="isPhoneValid = $event"
            />
          </div>
          <div class="checkout__delivery-mode-wrapper">
            <SfRadio
              v-if="store.orderConfig.pickupEnabled"
              label="Pickup from store"
              description="You can add any notes regarding your order"
              name="delivery-mode"
              value="pickup"
              :selected="deliveryMode"
              @input="$store.commit('checkout/setDeliveryMode', $event)"
            />
            <SfRadio
              v-if="store.orderConfig.deliveryEnabled"
              label="Get it delivered to you"
              description=""
              name="delivery-mode"
              value="ship"
              :selected="deliveryMode"
              @input="$store.commit('checkout/setDeliveryMode', $event)"
            />
          </div>
          <div v-if="deliveryMode === 'ship'" class="shipping-details">
            <SfHeading
              title="Shipping Details"
              :level="2"
              class="sf-heading--left sf-heading--no-underline title"
            />
            <div class="form checkout__form">
              <SfInput
                v-model="shippingDetails.streetName"
                :value="shippingDetails.streetName"
                label="Street name"
                name="streetName"
                class="form__element"
                :required="true"
                :valid="!$v.shippingDetails.streetName.$error"
                :error-message="$t('Field is required')"
                @blur="$v.shippingDetails.streetName.$touch()"
              />
              <SfInput
                v-model="shippingDetails.apartment"
                :value="shippingDetails.apartment"
                label="House/Apartment number"
                name="apartment"
                class="form__element"
                :required="true"
                :valid="!$v.shippingDetails.apartment.$error"
                :error-message="$t('Field is required')"
                @blur="$v.shippingDetails.apartment.$touch()"
              />
              <SfInput
                v-model="shippingDetails.city"
                :value="shippingDetails.city"
                label="City"
                name="city"
                class="form__element form__element--half"
                :required="true"
                :valid="!$v.shippingDetails.city.$error"
                :error-message="$t('Field is required')"
                @blur="$v.shippingDetails.city.$touch()"
              />
              <SfInput
                v-model="shippingDetails.state"
                :value="shippingDetails.state"
                label="State/Province"
                name="state"
                class="form__element form__element--half form__element--half-even"
                :required="true"
                :valid="!$v.shippingDetails.state.$error"
                :error-message="$t('Field is required')"
                @blur="$v.shippingDetails.state.$touch()"
              />
              <SfInput
                v-model="shippingDetails.zipCode"
                :value="shippingDetails.zipCode"
                label="Zip-code"
                name="zipCode"
                class="form__element form__element--half"
                :required="true"
                :valid="!$v.shippingDetails.zipCode.$error"
                :error-message="$t('Field is required')"
                @blur="$v.shippingDetails.zipCode.$touch()"
              />
              <SfSelect
                :selected="shippingDetails.country"
                @change="shippingDetails.country = $event"
                label="Country"
                class="form__element form__element--half form__element--half-even form__select sf-select--underlined"
                :required="true"
                :valid="!$v.shippingDetails.country.$error"
                :error-message="$t('Field is required')"
                @blur="$v.shippingDetails.country.$touch()"
              >
                <SfSelectOption
                  v-for="countryOption in countries"
                  :key="countryOption"
                  :value="countryOption"
                >
                  {{ countryOption }}
                </SfSelectOption>
              </SfSelect>
            </div>
          </div>
          <div v-if="deliveryMode === 'pickup'" class="pickup-details">
            <div class="form checkout__form">
              <ZoTextbox
                v-model="pickupDetails.notes"
                :value="pickupDetails.notes"
                label="Pickup notes"
                name="pickupNotes"
              />
            </div>
          </div>
          <SfHeading
            title="Order review"
            :level="2"
            class="sf-heading--left sf-heading--no-underline title"
          />
          <CartProduct
            v-for="product in productsInCart"
            :key="product.id"
            :product="product"
            :image="product.image"
            :title="product.title"
            :variant-name="product.variantName"
            :regular-price="product.basePrice"
            :special-price="product.discountedPrice"
            :qty="product.qty"
            :min-qty="1"
            :stock="product.stock"
            :show-stock-errors="store.orderConfig.allowOrderOnNoStock !== true"
            class="collected-product"
            @click:remove="removeHandler(product)"
            @input="changeQuantity(product, $event)"
            @hit-max-stock="showMaxStockNotification"
          >
          </CartProduct>
          <div class="summary">
            <div class="summary__content">
              <SfHeading
                title="Totals"
                :level="1"
                class="sf-heading--left sf-heading--no-underline summary__title"
              />
              <SfProperty
                name="Items"
                :value="numberOfItemsInCart"
                class="sf-property--full-width property"
              />
              <SfProperty
                v-for="total in totals"
                :key="total.code"
                class="sf-property--full-width o-microcart__total-price"
              >
                <template #name>
                  <span class="sf-property__name">{{ total.title }}</span>
                </template>
                <template #value>
                  <SfPrice
                    v-if="!total.isFree"
                    :regular="total.value | price"
                    class="sf-price--big"
                  />
                  <SfPrice
                    v-else
                    :regular="total.value | price"
                    special="Free"
                    class="sf-price--big"
                  />
                </template>
              </SfProperty>
              <!-- <SfCheckbox v-model="terms" name="terms" class="summary__terms">
                <template #label>
                  <div class="sf-checkbox__label">
                    I agree to <a href="#">Terms and conditions</a>
                  </div>
                </template>
              </SfCheckbox> -->
            </div>
          </div>
          <div class="actions">
            <SfButton
              class="sf-button--full-width actions__button"
              :disabled="!isFormValid"
              @click="placeOrder"
              >Place my order
            </SfButton>
            <SfButton
              class="sf-button--full-width sf-button--text color-secondary actions__button actions__button--secondary"
              @click="toCatalog()"
            >
              Go back
            </SfButton>
          </div>
        </div>
      </div>
      <order-success
        v-if="orderComplete"
        class="order-success"
        :order-id="orderId"
        :success-message="
          store.orderConfig.messageAfterSuccess || 'Order Placed'
        "
      ></order-success>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions, mapState } from "vuex";
import { notifications } from "@lib/modules/cart/helpers";
import { required, minLength, email } from "vuelidate/lib/validators";
import { ModalList } from "@theme/store/ui/modals";

import SfHeading from "@lib/components/atoms/SfHeading/SfHeading";
//import SfCheckbox from "@lib/components/atoms/SfCheckbox/SfCheckbox";
import SfButton from "@lib/components/atoms/SfButton/SfButton";
import SfProperty from "@lib/components/atoms/SfProperty/SfProperty";
import SfPrice from "@lib/components/atoms/SfPrice/SfPrice";
import SfInput from "@lib/components/atoms/SfInput/SfInput";
import CartProduct from "@lib/components/organisms/CartProduct";
import SfSelect from "@lib/components/molecules/SfSelect/SfSelect";
import SfRadio from "@lib/components/molecules/SfRadio/SfRadio";
import ZoTextbox from "@lib/components/atoms/Textbox";

import OrderSuccess from "@theme/components/Catalog/OrderSuccess";
import CountryCodes from "@lib/utility/countryCodes";
import PhoneInput from "@lib/components/atoms/PhoneInput";
import analytics from "@lib/utility/analytics";

export default {
  name: "Checkout",
  components: {
    CartProduct,
    SfHeading,
    //SfCheckbox,
    SfButton,
    SfProperty,
    SfPrice,
    SfInput,
    SfSelect,
    OrderSuccess,
    SfRadio,
    ZoTextbox,
    PhoneInput,
  },
  data() {
    return {
      personalDetails: {
        fullName: "",
        email: "",
        phone: {
          number: "",
          dialCode: "",
        },
      },
      shippingDetails: {
        streetName: "",
        apartment: "",
        city: "",
        state: "",
        zipCode: "",
        country: "",
      },
      pickupDetails: {
        notes: "",
      },
      pickupNotes: "",
      countries: Object.keys(CountryCodes)
        .map((x) => CountryCodes[x])
        .sort(),
      terms: false,
      orderComplete: false,
      orderId: null,
      isPhoneValid: false,
    };
  },
  validations() {
    let personalDetails = {
      fullName: {
        required,
        minLength: minLength(2),
      },
      email: {
        required,
        email,
      },
      phone: {
        required,
        complex(value) {
          return this.isPhoneValid;
        },
      },
    };
    let shippingDetails = {
      streetName: {
        required,
      },
      apartment: {
        required,
      },
      city: {
        required,
      },
      state: {
        required,
      },
      zipCode: {
        required,
      },
      country: {
        required,
      },
    };
    if (this.deliveryMode === "ship") {
      return { personalDetails, shippingDetails };
    } else {
      return { personalDetails };
    }
  },
  computed: {
    ...mapGetters({
      productsInCart: "cart/getCartItems",
      totals: "checkout/getTotals",
      isCartValid: "cart/isCartValid",
      numberOfItemsInCart: "cart/getNumberOfItems",
      store: "store",
      storeId: "storeId",
      deliveryMode: "checkout/getDeliveryMode",
      isLoggedIn: "user/isLoggedIn",
      phoneNumber: "user/phoneNumber",
      email: "user/email",
      fullName: "user/displayName",
    }),
    defaultCountry() {
      let country = "";
      if (
        this.store.orderConfig.enabledCountries &&
        this.store.orderConfig.enabledCountries.length
      ) {
        country = CountryCodes[this.store.orderConfig.enabledCountries[0]];
      }
      return country;
    },
    isFormValid() {
      let isValid = true;
      if (this.$v.personalDetails.$invalid) {
        isValid = false;
      }

      if (this.deliveryMode === "ship" && this.$v.shippingDetails.$invalid) {
        isValid = false;
      }

      if (!this.isCartValid) {
        isValid = false;
      }
      return isValid;
    },
  },
  watch: {
    isLoggedIn: {
      immediate: true,
      handler: function (isLoggedIn) {
        if (isLoggedIn) {
          this.personalDetails.fullName = this.fullName;
          this.personalDetails.email = this.email;
          this.personalDetails.phone.number = this.phoneNumber;
        }
      },
    },
    productsInCart: function (val) {
      if (val.length === 0 && !this.orderComplete) {
        this.toCatalog();
      }
    },
    defaultCountry: {
      immediate: true,
      handler: function (val) {
        this.shippingDetails.country = val;
      },
    },
  },
  methods: {
    ...mapActions({
      notify: "notification/spawnNotification",
      openModal: "ui/openModal",
    }),
    login() {
      this.openModal({ name: ModalList.Auth, payload: "login" });
    },
    toCatalog() {
      this.$router.push({
        name: "catalog",
        params: { storeId: this.$route.params.storeId },
      });
    },
    removeHandler(product) {
      this.$store.dispatch("cart/removeItem", { product: product });
    },
    changeQuantity(product, newQuantity) {
      this.$store.dispatch("cart/updateQuantity", {
        product: product,
        qty: newQuantity,
      });
    },
    prepareOrder() {
      let order = {};
      let customer = this.personalDetails;
      order.personalDetails = {
        fullName: customer.fullName,
        email: customer.email,
        phone: customer.phone ? customer.phone.number : "",
        phoneDialCode: customer.phone ? customer.phone.dialCode : "",
      };
      if (
        this.store.orderConfig.deliveryEnabled &&
        this.deliveryMode === "ship"
      ) {
        order.shippingDetails = this.shippingDetails;
      }
      if (
        this.store.orderConfig.pickupEnabled &&
        this.deliveryMode === "pickup"
      ) {
        order.pickupDetails = this.pickupDetails;
      }
      order.type = this.deliveryMode;
      order.totals = this.totals.map((x) => ({
        code: x.code,
        amount: x.isFree ? 0 : x.value,
        isPercentage: x.isPercentage,
        percentAmount: x.percentAmount,
      }));
      order.cart = [];
      for (let product of this.productsInCart) {
        let cartItem = { ...product };
        delete cartItem.category;
        order.cart.push(cartItem);
      }
      if (this.isLoggedIn) {
        order.userInfo = {
          email: this.email,
          phoneNumber: this.phoneNumber,
          fullName: this.fullName,
        };
      }
      return { sfId: this.storeId, order };
    },
    async placeOrder() {
      this.$store.commit("ui/setLoader", true);
      analytics.setUserEmail(this.personalDetails.email);
      let order = this.prepareOrder();
      if (order) {
        try {
          this.orderId = await this.$store.dispatch("order/createOrder", order);
          let successNotfication = notifications.createNotification({
            type: "success",
            message: `Order Placed. Order number: ${this.orderId}. Please note this number for reference.`,
          });
          this.orderComplete = true;
          this.$store.dispatch("cart/clear", { sync: true });
        } catch (error) {
          this.$store.dispatch(
            "notification/spawnNotification",
            notifications.createNotification({
              type: "danger",
              message:
                "Could not place your order. Please try again or contact store directly.",
            })
          );
        }
      }
      this.$store.commit("ui/setLoader", false);
    },
    showMaxStockNotification() {
      this.$store.dispatch(
        "notification/spawnNotification",
        notifications.createNotification({
          type: "danger",
          message: "Stock not available",
        })
      );
    },
  },
};
</script>
<style lang="scss" scoped>
@import "~@lib/styles";
#checkout {
  box-sizing: border-box;
  @include for-desktop {
    padding: 0 var(--spacer-sm);
    max-width: 1272px;
    margin: 0 auto;
  }
  @include for-mobile {
    // margin: var(--spacer-sm) var(--spacer-xs);
    border: 1px solid #e5e5e5;
    background: white;
  }
}
.checkout {
  --steps-content-padding: 0 var(--spacer-sm);
  --heading-padding: 0 0 0.75rem 0;
  --property-name-font-weight: 600;
  --property-value-font-size: 1.2rem;
  @include for-desktop {
    --steps-content-padding: 0;
    display: flex;
  }

  .log-in {
    text-align: center;
    padding: 16px 0;

    &__button {
      margin: 0 auto;
    }
  }

  &__main {
    @include for-desktop {
      flex: 1;
      padding: var(--spacer-xl) 0 0 0;
    }

    @include for-mobile {
      padding: var(--spacer-sm);
    }
  }
  &__aside {
    @include for-desktop {
      flex: 0 0 26.8125rem;
      margin: 0 0 0 var(--spacer-xl);
    }
    &-order {
      box-sizing: border-box;
      width: 100%;
      box-shadow: 0px 4px 11px rgba(var(--c-dark-base), 0.1);
      background: var(--c-light);
      padding: var(--spacer-xl) calc(var(--spacer-lg) * 2);
    }
  }

  &__form {
    margin-bottom: 0.5rem;
  }

  &__delivery-mode-wrapper {
    margin: 0.5rem 0;
  }

  .shipping-details {
    margin: 1rem 0;
  }

  .summary {
    padding: 1rem 0;
  }

  .order-success {
    width: 100%;
  }

  .actions {
    padding: 0 0 1rem 0;

    .sf-button {
      margin-bottom: 0.5rem;
    }
  }

  .sf-property {
    padding: 0 0 0.5rem 0;

    &__name {
      font-weight: 600;
    }
  }

  .collected-product {
    border-bottom: 1px solid #ccc;
  }
}
</style>
